import { Bid } from 'shared-lib';

// Function to determine the display status of a bid
export const getDisplayStatus = (bid: Bid | null): string => {
  if (!bid) return 'no-bid';
  
  // If bid has intent='ignore'
  if (bid.intent === 'ignore') return 'ignored';
  
  // Otherwise use status from bid with intent='bid'
  return bid.status || 'pending';
};

// Function to get user-friendly display text for a bid status
export const getBidStatusLabel = (bid: Bid | null): string => {
  const status = getDisplayStatus(bid);
  
  // Map internal statuses to user-friendly labels
  switch (status) {
    case 'pending':
      return 'Sent';
    case 'accepted':
      return 'Won';
    case 'rejected':
      return 'Not Selected';
    case 'no-bid':
      return 'No Bid';
    case 'ignored':
      return 'Ignored';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
};

// Get color for bid status chip
export const getBidStatusColor = (bid: Bid | null) => {
  const status = getDisplayStatus(bid);
  
  switch (status) {
    case 'pending':
      return 'warning';
    case 'accepted':
      return 'success';
    case 'rejected':
    case 'ignored':
      return 'error';
    default:
      return 'default';
  }
};

// Get text color for status chip
export const getStatusColor = (bid: Bid | null): string => {
  const status = getDisplayStatus(bid);
  
  switch (status) {
    case 'pending':
      return '#f57c00'; // Orange
    case 'accepted':
      return '#00AB9B'; // Teal
    case 'rejected':
    case 'ignored':
      return '#d32f2f'; // Red
    default:
      return 'rgba(16, 29, 59, 0.6)'; // Dark blue (secondary text)
  }
};

// Get background color for status chip
export const getStatusBackground = (bid: Bid | null): string => {
  const status = getDisplayStatus(bid);
  
  switch (status) {
    case 'pending':
      return 'rgba(245, 124, 0, 0.1)'; // Light orange
    case 'accepted':
      return 'rgba(0, 171, 155, 0.1)'; // Light teal
    case 'rejected':
    case 'ignored':
      return 'rgba(211, 47, 47, 0.1)'; // Light red
    default:
      return 'rgba(16, 29, 59, 0.05)'; // Light gray
  }
};

// Format field name to be more readable
export const formatFieldName = (fieldName: string): string => {
  // Handle camelCase - insert space before capital letters
  let formatted = fieldName.replace(/([A-Z])/g, ' $1');
  
  // Handle snake_case - replace underscores with spaces
  formatted = formatted.replace(/_/g, ' ');
  
  // Trim and capitalize each word
  return formatted
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Format service details into a human-readable summary
export const formatServiceDetails = (serviceDetails: any): string => {
  if (!serviceDetails) return 'No details available';
  
  // Try to parse JSON description for new model
  try {
    // If serviceDetails is a string, try to parse it as JSON
    const details = typeof serviceDetails === 'string' 
      ? JSON.parse(serviceDetails) 
      : serviceDetails;
    
    // For the new model with JSON description
    if (details.description && typeof details.description === 'string') {
      try {
        const parsedDescription = JSON.parse(details.description);
        
        // If we have services data, extract key points
        if (parsedDescription.services && typeof parsedDescription.services === 'object') {
          const serviceNames = Object.keys(parsedDescription.services);
          
          if (serviceNames.length > 0) {
            // Get main service details
            const mainService = parsedDescription.services[serviceNames[0]];
            const highlightDetails: string[] = [];
            
            // Extract a few important fields from the details
            Object.entries(mainService).slice(0, 2).forEach(([key, value]) => {
              highlightDetails.push(`${formatFieldName(key)}: ${value}`);
            });
            
            return highlightDetails.join(', ');
          }
        }
        
        // Fallback to using water location or service description
        if (parsedDescription.waterLocation) {
          return `Location: ${parsedDescription.waterLocation}`;
        }
        
        if (parsedDescription.serviceDescription) {
          const desc = parsedDescription.serviceDescription;
          return desc.length > 50 ? `${desc.substring(0, 50)}...` : desc;
        }
      } catch (e) {
        // If JSON parsing fails, use the raw description
        const desc = details.description;
        return desc.length > 50 ? `${desc.substring(0, 50)}...` : desc;
      }
    }
    
    // For old format, try to extract key details
    const keys = Object.keys(details);
    if (keys.length > 0) {
      const highlightDetails: string[] = [];
      
      // Extract up to 2 key details
      Object.entries(details).slice(0, 2).forEach(([key, value]) => {
        if (key !== 'attachments' && key !== 'photoUrls' && key !== 'description') {
          highlightDetails.push(`${formatFieldName(key)}: ${value}`);
        }
      });
      
      if (highlightDetails.length > 0) {
        return highlightDetails.join(', ');
      }
    }
    
    // If we couldn't extract specific details, return a generic message
    return 'Details available';
    
  } catch (e) {
    // If all else fails, try to convert to string
    return 'Details available';
  }
}; 